export type ModalSize = "sm" | "md" | "lg" | "xl" | "full85";

export type ModalOptions = {
	size?: ModalSize;
	onClose?: () => void;
};

/** Represents a single modal entry in the stack, holding its JSX content, controller, size, and onClose callback. */
export class ModalElement {
	content: JSX.Element;
	controller = new ModalController();
	size: ModalSize;
	onClose?: () => void;

	constructor(content: JSX.Element, controller: ModalController, size: ModalSize = "xl", onClose?: () => void) {
		this.content = content;
		this.controller = controller;
		this.size = size;
		this.onClose = onClose;
	}
}

/** Controls a single modal instance, providing a unique id, display state, and a pop method to dismiss it. */
export class ModalController {
	id: number = Math.floor((Math.random() + 1) * 9898989);
	display: boolean = true;
	pop = () => {
		modalManager.pop(this.id);
	};
}

export interface ModalProps {
	close?: () => void;
}

/**
 * Global modal stack, deliberately OUTSIDE React state so any code (helpers, API error handlers,
 * event callbacks) can open a modal without a hook or provider at the call site. The rendering
 * layer registers a callback and re-renders on notify(); everything else just calls push/pop on
 * the `modalManager` singleton.
 */
class ModalManager {
	callbacks: Array<() => void> = [];
	elements: Array<ModalElement> = [];
	push = (builder: (controller: ModalController) => JSX.Element, options?: ModalOptions) => {
		const controller = new ModalController();
		const elem = new ModalElement(builder(controller), controller, options?.size ?? "xl", options?.onClose);

		this.elements.push(elem);

		this.notify();
	};
	notify = () => {
		for (const call of this.callbacks) {
			call();
		}
	};
	pop = (id: number) => {
		for (const i of this.elements) {
			if (i.controller.id === id && i.controller.display === true) {
				i.controller.display = false;
				i.onClose?.();
				this.notify();
				// Flip display first, remove later: the modal stays mounted (display=false) long
				// enough for the close transition to play; removing immediately would cut it off.
				setTimeout(() => {
					this.elements = this.elements.filter((x) => x.controller.id !== id);
				}, 1000);
			}
		}
	};
}

export const modalManager = new ModalManager();
